import { useNode } from "@craftjs/core";
import React, { useCallback } from "react";
import {
  ColorInput,
  SelectInput,
  UnitInput,
  ToggleInput,
  PaddingInput,
  MarginInput,
  Panel,
  Tabs,
  RangeNumberInput,
  Spacer,
  RichTextFormatting,
} from "../../input";
import { supportLibrary } from "../../block-supports";
import { isUpdatedFromDefault } from "../../utils";
import { debounce } from "lodash";

export const TableSettings = () => {
  const {
    id,
    actions: { setProp },
    props,
    dom,
    supports,
  } = useNode((node) => ({
    id: node.id,
    text: node.data.props.text,
    props: node.data.props,
    dom: node.dom,
    supports: node.data?.type?.craft?.related?.supports,
  }));

  const {
    table,
    tableHeader,
    tableFooter,
    typoSize,
    typoSizeTablet,
    typoSizeMobile,
    typoLineHeight,
    typoLetterSpacing,
    typoWordSpacing,
    typoTransform,
    typoStyle,
    typoDecoration,
    typoWeight,
    textColor,
    backgroundColor,
    headerTextColor,
    headerBackgroundColor,
    footerTextColor,
    footerBackgroundColor,
    layoutPadding,
    layoutPaddingTablet,
    layoutPaddingMobile,
    layoutMargin,
    layoutMarginTablet,
    layoutMarginMobile,
  } = props;

  const [internalRows, setInternalRows] = React.useState(table?.length ?? 0);
  const [internalCols, setInternalCols] = React.useState(
    table?.[0]?.length ?? 0,
  );

  // Throttle rows and column change
  const onRowsChange = (val) => {
    var currVal = Number(val);
    const diffRows = currVal - (table?.length ?? 0);
    if (currVal < (table?.length ?? 1)) {
      // deleting row
      setProp((props) => {
        var temp = [...(table ? table : [])]; // 2d
        temp.splice(-1, 1);
        props.table = temp;
        return props;
      });
    } else {
      // adding row
      setProp((props) => {
        var temp = [...(table ? table : [])]; // 2d
        for (let i = 0; i < diffRows; i++) {
          temp.push(new Array(table?.[0].length ?? 1).fill(""));
        }
        props.table = temp;
        return props;
      });
    }
  };

  const onColsChange = (val) => {
    var currVal = Number(val);
    const diffCols = currVal - (table?.[0].length ?? 1);
    setProp((props) => {
      // deleting cols
      if (currVal < (table?.[0].length ?? 1)) {
        var newArr = [...(table ? table : [])].map((subArr) => {
          return subArr.slice(0, diffCols);
        });
        if (tableHeader) {
          var tempHeader = [...tableHeader];
          tempHeader.splice(-1, 1);
          props.tableHeader = tempHeader;
        }
        if (tableFooter) {
          var tempFooter = [...tableFooter];
          tempFooter.splice(-1, 1);
          props.tableFooter = tempFooter;
        }
        props.table = newArr;

        return props;
      } else {
        // adding cols
        var newArr = [...table].map((row) => {
          return [...row, ...new Array(diffCols).fill("")];
        });
        props.table = newArr;
        // create dynamic header and footer
        if (tableHeader && tableHeader.length > 0) {
          var tempHeader = [...tableHeader, ""];
          props.tableHeader = tempHeader;
        }
        if (tableFooter && tableFooter.length > 0) {
          var tempFooter = [...tableFooter, ""];
          props.tableFooter = tempFooter;
        }
        return props;
      }
    });
  };

  // Debounce onRowsChange and onColsChange. Making sure that the table is added as dependency
  const onRowsChangeThrottle = useCallback(
    debounce((val) => onRowsChange(val), 300),
    [table],
  );

  const onColsChangeThrottle = useCallback(
    debounce((val) => onColsChange(val), 300),
    [table, tableHeader, tableFooter],
  );

  function GeneralSettings() {
    return (
      <div className="g-settings__wrapper">
        <Spacer spacing="1rem">
          <RangeNumberInput
            resetValue={undefined}
            value={internalCols}
            onChange={(val) => {
              setInternalCols(val);
              onColsChangeThrottle(val);
            }}
            min={1}
            max={15}
            step={1}
            label={"Total Columns"}
          />
          <RangeNumberInput
            resetValue={undefined}
            value={internalRows}
            onChange={(val) => {
              setInternalRows(val);
              onRowsChangeThrottle(val);
            }}
            min={1}
            max={200}
            step={1}
            label={"Total Rows"}
          />
          <ToggleInput
            resetValue={undefined}
            label="Table Header"
            value={tableHeader && tableHeader.length > 0}
            onChange={(val) => {
              if (val) {
                setProp((props) => {
                  props.tableHeader = Array(table[0].length).fill("");
                  return props;
                });
              } else {
                setProp((props) => {
                  props.tableHeader = undefined;
                });
              }
            }}
          />
          <ToggleInput
            resetValue={undefined}
            label="Table Footer"
            value={tableFooter && tableFooter.length > 0}
            onChange={(val) => {
              if (val) {
                setProp((props) => {
                  props.tableFooter = new Array(table[0].length).fill("");
                  return props;
                });
              } else {
                setProp((props) => {
                  props.tableFooter = undefined;
                });
              }
            }}
          />
        </Spacer>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: typoSize },
            { key: typoSizeTablet },
            { key: typoSizeMobile },
            { key: typoLineHeight },
            { key: typoLetterSpacing },
            { key: typoWordSpacing },
            { key: typoWeight },
            { key: typoTransform },
            { key: typoDecoration },
            { key: typoStyle },
          ])}
          id={`block-setting-${id}-typography`}
          title={"Typography"}
          initialOpen={false}
        >
          <UnitInput
            isResponsive={true}
            resetValue={undefined}
            value={typoSize}
            valueTablet={typoSizeTablet}
            valueMobile={typoSizeMobile}
            onChange={(val) => {
              setProp((props) => {
                props.typoSize = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.typoSizeTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.typoSizeMobile = val;
              });
            }}
            label={"Size"}
          />
          <UnitInput
            resetValue={undefined}
            value={typoLineHeight}
            onChange={(val) => {
              setProp((props) => {
                props.typoLineHeight = val;
              });
            }}
            label={"Line Height"}
          />
          <UnitInput
            resetValue={undefined}
            value={typoLetterSpacing}
            onChange={(val) => {
              setProp((props) => {
                props.typoLetterSpacing = val;
              });
            }}
            label={"Letter Spacing"}
          />
          <UnitInput
            resetValue={undefined}
            value={typoWordSpacing}
            onChange={(val) => {
              setProp((props) => {
                props.typoWordSpacing = val;
              });
            }}
            label={"Word Spacing"}
          />
          <SelectInput
            resetValue={undefined}
            value={typoWeight}
            onChange={(val) => {
              setProp((props) => {
                props.typoWeight = val;
              });
            }}
            options={[
              { label: "100", value: "100" },
              { label: "200", value: "200" },
              { label: "300", value: "300" },
              { label: "400", value: "400" },
              { label: "500", value: "500" },
              { label: "600", value: "600" },
              { label: "700", value: "700" },
              { label: "800", value: "800" },
              { label: "900", value: "900" },
            ]}
            label="Weight"
          />
          <SelectInput
            resetValue={undefined}
            value={typoTransform}
            onChange={(val) => {
              setProp((props) => {
                props.typoTransform = val;
              });
            }}
            options={[
              { label: "Uppercase", value: "uppercase" },
              { label: "Lowercase", value: "lowercase" },
              { label: "Capitalize", value: "capitalize" },
              { label: "None", value: "none" },
            ]}
            label="Transform"
          />
          <SelectInput
            resetValue={undefined}
            value={typoDecoration}
            onChange={(val) => {
              setProp((props) => {
                props.typoDecoration = val;
              });
            }}
            options={[
              { label: "Underline", value: "underline" },
              { label: "Overline", value: "overline" },
              { label: "None", value: "none" },
            ]}
            label="Decoration"
          />
          <SelectInput
            resetValue={undefined}
            value={typoStyle}
            onChange={(val) => {
              setProp((props) => {
                props.typoStyle = val;
              });
            }}
            options={[
              {
                label: "Normal",
                value: "normal",
              },
              {
                label: "Italic",
                value: "italic",
              },
            ]}
            label="Style"
          />
        </Panel>
        <RichTextFormatting />
      </div>
    );
  }

  function StyleSettings() {
    return (
      <>
        <div className="g-settings__wrapper">
          <Panel
            isUpdated={isUpdatedFromDefault([
              { key: textColor },
              { key: backgroundColor },
              { key: headerTextColor },
              { key: headerBackgroundColor },
              { key: footerTextColor },
              { key: footerBackgroundColor },
            ])}
            id={`block-setting-${id}-colors`}
            title={"Colors"}
            initialOpen={true}
          >
            <ColorInput
              resetValue={undefined}
              value={textColor}
              onChange={(val) => {
                setProp((props) => {
                  props.textColor = val;
                });
              }}
              label={"Text"}
            />
            <ColorInput
              resetValue={undefined}
              value={backgroundColor}
              onChange={(val) => {
                setProp((props) => {
                  props.backgroundColor = val;
                });
              }}
              label={"Background"}
            />
            <ColorInput
              resetValue={undefined}
              value={headerTextColor}
              onChange={(val) => {
                setProp((props) => {
                  props.headerTextColor = val;
                });
              }}
              label={"Header Text"}
            />

            <ColorInput
              resetValue={undefined}
              value={headerBackgroundColor}
              onChange={(val) => {
                setProp((props) => {
                  props.headerBackgroundColor = val;
                });
              }}
              label={"Header Background"}
            />
            <ColorInput
              resetValue={undefined}
              value={footerTextColor}
              onChange={(val) => {
                setProp((props) => {
                  props.footerTextColor = val;
                });
              }}
              label={"Footer Text"}
            />
            <ColorInput
              resetValue={undefined}
              value={footerBackgroundColor}
              onChange={(val) => {
                setProp((props) => {
                  props.footerBackgroundColor = val;
                });
              }}
              label={"Footer Background"}
            />
          </Panel>
        </div>
      </>
    );
  }

  function AdvancedSettings() {
    return (
      <>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: layoutMargin },
            { key: layoutMarginTablet },
            { key: layoutMarginMobile },
            { key: layoutPadding },
            { key: layoutPaddingTablet },
            { key: layoutPaddingMobile },
          ])}
          id={`block-setting-${id}-layout`}
          title={"Layout"}
          initialOpen={true}
        >
          <MarginInput
            isResponsive={true}
            resetValue={undefined}
            value={layoutMargin}
            valueTablet={layoutMarginTablet}
            valueMobile={layoutMarginMobile}
            onChange={(val) => {
              setProp((props) => {
                props.layoutMargin = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.layoutMarginTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.layoutMarginMobile = val;
              });
            }}
            label={"Margin"}
          />
          <PaddingInput
            isResponsive={true}
            resetValue={undefined}
            value={layoutPadding}
            valueTablet={layoutPaddingTablet}
            valueMobile={layoutPaddingMobile}
            onChange={(val) => {
              setProp((props) => {
                props.layoutPadding = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.layoutPaddingTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.layoutPaddingMobile = val;
              });
            }}
            label={"Padding"}
          />
        </Panel>
        {supports &&
          supports.map((support) => {
            var Component = supportLibrary[support]?.component;

            if (!Component) {
              return;
            }

            return (
              <>
                <Component
                  block={{
                    id,
                    props,
                    dom,
                  }}
                  actions={{
                    setProp,
                  }}
                ></Component>
              </>
            );
          })}
      </>
    );
  }
  const tabs = [
    {
      label: "General",
      key: "general",
      children: GeneralSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: table },
        { key: tableHeader },
        { key: tableFooter },
        { key: typoSize },
        { key: typoSizeTablet },
        { key: typoSizeMobile },
        { key: typoLineHeight },
        { key: typoLetterSpacing },
        { key: typoWordSpacing },
        { key: typoWeight },
        { key: typoTransform },
        { key: typoDecoration },
        { key: typoStyle },
      ]),
    },
    {
      label: "Style",
      key: "style",
      children: StyleSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: textColor },
        { key: backgroundColor },
        { key: headerTextColor },
        { key: headerBackgroundColor },
        { key: footerTextColor },
        { key: footerBackgroundColor },
      ]),
    },
    {
      label: "Advanced",
      key: "advanced",
      children: AdvancedSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: layoutMargin },
        { key: layoutMarginTablet },
        { key: layoutMarginMobile },
        { key: layoutPadding },
        { key: layoutPaddingTablet },
        { key: layoutPaddingMobile },
      ]),
    },
  ];

  return (
    <div>
      <Tabs
        type="block-setting"
        id={`block-settings-tab-${id}`}
        items={tabs.map((item, i) => {
          return {
            label: item.label,
            key: item.key,
            children: item.children,
          };
        })}
      />
    </div>
  );
};
